// src/features/captions/useCaptionsEligibility.ts

import { useAuth } from '@/features/auth/hooks/useAuth';
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags';
import { resolveCaptionsEligibility } from './eligibility';

export interface CaptionsEligibility {
  eligible: boolean;
}

/**
 * THE single seam for live automated captions visibility — every caption UI
 * surface (nav entry, /captions page, stream-settings caption fields, the
 * sub-account grant toggle) consumes this hook; nothing renders when it says
 * no. Resolution: build flag AND media access AND the SELECTED account's
 * `allowAutomatedCaptions` entitlement (`selectedCustomer.cfg.quota...` — the
 * entitlement is granted per account, so it must follow the account selector,
 * NOT the login account like Legacy Reports' cohort verdict does).
 *
 * The permission input mirrors the Legacy Reports precedent's reasoning
 * (gate on the permission that gates the surface's nav section) but lands on
 * `canViewMedia` (serviceRead OR the subcustomer pair) rather than raw
 * serviceRead: caption surfaces live in the Media section, whose gate is
 * PermissionUtils.hasMediaAccess. Absent entitlement fails CLOSED (see
 * resolveCaptionsEligibility). UX gating only — the server rejects
 * unentitled calls on every /v1/captions route.
 */
export function useCaptionsEligibility(): CaptionsEligibility {
  const { selectedCustomer, canViewMedia } = useAuth();
  return {
    eligible: resolveCaptionsEligibility({
      flagEnabled: FEATURE_FLAGS.captionsEnabled,
      mediaAccess: canViewMedia,
      allowAutomatedCaptions:
        selectedCustomer?.cfg.quota.features.account.allowAutomatedCaptions,
    }),
  };
}
